// src/pages/instrument-details/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import Button from '../../components/ui/Button';
import Icon from '../../components/ui/AppIcon.jsx';

import MeasurementEditor from './components/MeasurementEditor.jsx';

import {
  getInstrument,
  listCalibrations,
  createCalibration,
  listMeasurements,
} from '../../utils/api';

import { listBlocks } from '../../utils/api-blocks';
import { supabase } from '../../utils/supabase';
import { resolveCalibrationRoute, resolveCalibrationFamily } from '../../utils/calibration-routing';
import {
  listElectricalSessionsByInstrument,
  listElectricalBlocks,
  listElectricalResults,
} from '../../utils/api-electrical-calibration';
import { generateElectricalNoSacCertificatePdf } from '../../utils/electrical-certificate-pdf';
import { listCertificatesByInstrument } from '../../utils/api-certificates';
import CertLabelModal from '../../components/CertLabelModal';
import ExternalCertUploadModal from '../../components/ExternalCertUploadModal';

// ✅ IMPORTADO: Modal de edición moderno desde 'ingresos'
import IntakeInstrumentModal from '../ingresos/components/IntakeInstrumentModal.jsx';

const BUCKET = 'instrument-attachments';

const InstrumentDetails = () => {
  const { instrumentId: routeInstrumentId, serviceOrderId } = useParams();
  const instrumentId = routeInstrumentId || serviceOrderId || null;


  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const calibrationId = searchParams.get('calibrationId');
  const view = searchParams.get('view') || (calibrationId ? 'editor' : 'details');

  const [instrument, setInstrument] = useState(null);
  const [cals, setCals] = useState([]);
  const [lastCalMeasurements, setLastCalMeasurements] = useState([]);
  const [lastCalUsed, setLastCalUsed] = useState(null);
  const [lastCalBlocks, setLastCalBlocks] = useState([]);
  const [lastRemitoInfo, setLastRemitoInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingRemitoInfo, setLoadingRemitoInfo] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ✅ ESTADO: Controla el modal de edición
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [startCalibrationFlow, setStartCalibrationFlow] = useState(false);

  // Certificados emitidos (registro nuevo) + etiqueta QR para reimprimir.
  const [certs, setCerts] = useState([]);
  const [labelFor, setLabelFor] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadedNumber, setUploadedNumber] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        if (!instrumentId || instrumentId === 'undefined') {
          throw new Error('ID de instrumento inválido.');
        }

        const inst = await getInstrument(instrumentId);
        setInstrument(inst);

        // Calibraciones del instrumento (ordenadas)
        const all = await listCalibrations();
        const mine = (all || [])
          .filter(r => r.instrument?.id === instrumentId)
          .sort((a, b) => new Date(b.date || 0) - new Date(a, b));
        setCals(mine);

        // Buscar la última calibración con mediciones
        const rows = await findLatestMeasurementsFor(mine);
        setLastCalMeasurements(rows.measurements || []);
        setLastCalUsed(rows.cal || null);

        // Cargar bloques si hay calibración con mediciones
        if (rows.cal) {
          try {
            const blocks = await listBlocks(rows.cal.id);
            setLastCalBlocks(blocks || []);
          } catch (e) {
            console.warn('[InstrumentDetails] Error cargando bloques:', e);
            setLastCalBlocks([]);
          }
        } else {
          setLastCalBlocks([]);
        }

        // Cargar adjuntos
        await loadFiles(instrumentId);
        await loadLastRemitoInfo(instrumentId);
      } catch (e) {
        console.error('[InstrumentDetails] load error:', e);
        setErr(e?.message || 'Error al cargar');
      } finally {
        setLoading(false);
      }
    })();
  }, [instrumentId]);

  // Si cambian las calibraciones, recalcular última con mediciones
  useEffect(() => {
    (async () => {
      if (!cals?.length) {
        setLastCalMeasurements([]);
        setLastCalUsed(null);
        setLastCalBlocks([]);
        return;
      }
      const rows = await findLatestMeasurementsFor(cals);
      setLastCalMeasurements(rows.measurements || []);
      setLastCalUsed(rows.cal || null);

      // Cargar bloques de la última calibración con mediciones
      if (rows.cal) {
        try {
          const blocks = await listBlocks(rows.cal.id);
          setLastCalBlocks(blocks || []);
        } catch (e) {
          console.warn('[InstrumentDetails] Error cargando bloques:', e);
          setLastCalBlocks([]);
        }
      } else {
        setLastCalBlocks([]);
      }
    })();
  }, [cals]);

  // Encuentra la última calibración que tenga al menos 1 medición
  async function findLatestMeasurementsFor(calList) {
    const ordered = (calList || []).slice().sort((a, b) => new Date(b.date || 0) - new Date(a, b));
    for (const cal of ordered) {
      try {
        const m = await listMeasurements(cal.id);
        if (m && m.length) {
          return { cal, measurements: m };
        }
      } catch (e) {
        console.warn('[InstrumentDetails] listMeasurements error for cal', cal?.id, e);
      }
    }
    return { cal: null, measurements: [] };
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Archivos (instrument_files + Storage URLs)
  // ────────────────────────────────────────────────────────────────────────────
  async function loadFiles(id) {
    try {
      setLoadingFiles(true);
      const { data, error } = await supabase
        .from('instrument_files')
        .select('id, path, name, size, mime, created_at, uploaded_by')
        .eq('instrument_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Obtener URLs (si el bucket es público, publicUrl; si no, firmada)
      const withUrls = await Promise.all(
        (data || []).map(async (f) => {
          // pública
          const pub = supabase.storage.from(BUCKET).getPublicUrl(f.path);
          let url = pub?.data?.publicUrl || null;

          // si no fuera público, firmamos
          if (!url) {
            const { data: signed } = await supabase
              .storage
              .from(BUCKET)
              .createSignedUrl(f.path, 3600);
            url = signed?.signedUrl || null;
          }

          return { ...f, url };
        })
      );

      setFiles(withUrls);
    } catch (e) {
      console.error('[InstrumentDetails] loadFiles error:', e);
    } finally {
      setLoadingFiles(false);
    }
  }

  async function loadLastRemitoInfo(id) {
    try {
      setLoadingRemitoInfo(true);
      setLastRemitoInfo(null);

      const { data: remitoItemRows, error: itemsErr } = await supabase
        .from('remito_items')
        .select('remito_id')
        .eq('instrument_id', id);

      if (itemsErr) throw itemsErr;

      const remitoIds = Array.from(
        new Set((remitoItemRows || []).map((row) => row.remito_id).filter(Boolean))
      );

      if (!remitoIds.length) {
        setLastRemitoInfo(null);
        return;
      }

      const { data: remitos, error: remitosErr } = await supabase
        .from('remitos')
        .select('id, code, official_number, date_out, status')
        .in('id', remitoIds)
        .order('date_out', { ascending: false });

      if (remitosErr) throw remitosErr;

      const latest = (remitos || [])[0] || null;
      if (!latest) {
        setLastRemitoInfo(null);
        return;
      }

      setLastRemitoInfo({
        id: latest.id,
        code: latest.official_number || latest.code || '—',
        date_out: latest.date_out || null,
        status: latest.status || null,
        count: remitoIds.length,
      });
    } catch (e) {
      console.error('[InstrumentDetails] loadLastRemitoInfo error:', e);
      setLastRemitoInfo(null);
    } finally {
      setLoadingRemitoInfo(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────────────────
  const toValidDate = (v) => {
    if (!v) return null;
    // Una fecha sola ("2026-07-14") la parsea como medianoche UTC y, al mostrarla
    // en hora local (UTC-3), retrocedía un día. Se ancla al mediodía local.
    const s = String(v);
    const soloFecha = /^\d{4}-\d{2}-\d{2}$/.test(s);
    const d = new Date(soloFecha ? `${s}T12:00:00` : v);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (v) => {
    const d = toValidDate(v);
    return d
      ? d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';
  };

  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const val = (bytes / Math.pow(k, i)).toFixed(2);
    return `${val} ${sizes[i]}`;
  };

  const nextDue = useMemo(
    () => instrument?.nextcalibrationdate || null,
    [instrument]
  );
  const lastCalDate = useMemo(
    () => (cals?.[0]?.date || instrument?.lastcalibrationdate || null),
    [cals, instrument]
  );

  const isExpired = (dateLike) => {
    const d = toValidDate(dateLike);
    return d ? d < new Date() : false;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success border-success/20';
      case 'maintenance': return 'bg-warning/10 text-warning border-warning/20';
      case 'expired': return 'bg-error/10 text-error border-error/20';
      case 'delivered_without_certificate': return 'bg-orange-500/10 text-orange-300 border-orange-500/20';
      default: return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'maintenance': return 'Settings';
      case 'expired': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const getDateLabel = (value) => {
    if (!value) return '—';
    return formatDate(value);
  };

  const getMovementStatusLabel = (status) => {
    const map = {
      borrador: 'Borrador',
      enviado: 'Enviado',
      cerrado: 'Cerrado',
      anulado: 'Anulado',
      active: 'Activo',
    };
    return map[status] || status || '—';
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Vigente';
      case 'maintenance':
        return 'Mantenimiento';
      case 'expired':
        return 'Vencido';
      case 'in_progress':
        return 'En proceso';
      case 'calibrated':
        return 'Calibrado';
      case 'rejected':
        return 'Rechazado';
      case 'with_certificate':
        return 'Con certificado';
      case 'without_certificate':
        return 'Sin certificado';
      case 'delivered_without_certificate':
        return 'Entregado sin certificado';
      case 'delivered':
        return 'Entregado';
      default:
        return status || '—';
    }
  };

  const openEditor = (calId) => {
    const n = new URLSearchParams(searchParams);
    n.set('calibrationId', calId);
    n.set('view', 'editor');
    setSearchParams(n, { replace: false });
  };

  const openDetails = () => {
    const n = new URLSearchParams(searchParams);
    n.delete('calibrationId');
    n.set('view', 'details');
    setSearchParams(n, { replace: false });
  };

  // 🔙 handler para volver al origen (service-orders o panel)
  const handleBackToOrigin = () => {
    const rt = searchParams.get('returnTo');
    if (rt) {
      navigate(rt);
    } else {
      navigate('/operations-overview');
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Acciones
  // ────────────────────────────────────────────────────────────────────────────
  const handleNuevaCalibracion = async () => {
    // Familias con módulo propio (eléctrica/presión) → su pantalla de captura,
    // no el editor genérico viejo (que no aplica a un multímetro).
    const route = resolveCalibrationRoute(instrument, { serviceOrderCode: searchParams.get('serviceOrderCode') || '' });
    if (route) { navigate(route); return; }
    try {
      setCreating(true);

      // Obtener usuario actual para asignar técnico y snapshot del nombre
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;

      const techName =
        user?.user_metadata?.full_name ||
        user?.email ||
        'Técnico sin nombre';

      const cal = await createCalibration({
        instrument_id: instrumentId,
        date: new Date().toISOString(),
        technician_id: user?.id || null,     // 👈 asigna técnico
        technician_name: techName,           // 👈 snapshot legible
        status: 'in-progress',
      });

      openEditor(cal.id);
    } catch (e) {
      console.error('[Nueva Calibración] error', e);
      alert(`No se pudo crear la calibración: ${e?.message || e}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDescargarCertificado = async () => {
    try {
      setDownloading(true);

      // Instrumento eléctrico: el certificado no se guarda como archivo, se
      // (re)genera desde la última sesión con resultados (módulo nuevo).
      if (resolveCalibrationFamily(instrument) === 'electrical') {
        const sessions = await listElectricalSessionsByInstrument(instrumentId);
        const session =
          (sessions || []).find((s) => ['calculated', 'closed'].includes(s.status)) || sessions?.[0];
        if (session) {
          const [blocks, results] = await Promise.all([
            listElectricalBlocks(session.id),
            listElectricalResults(session.id),
          ]);
          if (results?.length) {
            await generateElectricalNoSacCertificatePdf(
              { session, instrument, blocks, results },
              {
                serviceOrderCode: session.service_order_code || '',
                filename: `${session.certificate_number || instrument?.lab_id || 'certificado'}.pdf`,
              }
            );
            return;
          }
        }
        alert('Este multímetro todavía no tiene una calibración eléctrica con resultados. Iniciá y calculá una calibración primero.');
        return;
      }

      // Resto (presión / modelo anterior): abrir el certificado guardado.
      const url = cals?.[0]?.certificate_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('No hay certificado disponible para la última calibración.');
      }
    } catch (e) {
      console.error('[Descargar Certificado] error', e);
      alert(`No se pudo descargar el certificado: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  // ✅ MODIFICADO: Abre el modal de edición
  // Certificados emitidos por el sistema nuevo (los aprueba el jefe).
  useEffect(() => {
    if (!instrumentId) return;
    let alive = true;
    listCertificatesByInstrument(instrumentId)
      .then((r) => { if (alive) setCerts(r || []); })
      .catch((e) => console.error('[InstrumentDetails] certificados', e));
    return () => { alive = false; };
  }, [instrumentId]);

  const handleEditarInformacion = () => {
    setStartCalibrationFlow(false);
    setEditModalOpen(true);
  };

  const handleStartCalibration = () => {
    // Ir DIRECTO a la pantalla de calibración según la magnitud (antes abría el
    // modal de "Editar datos" y solo navegaba si guardabas → parecía roto).
    const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
    const route = resolveCalibrationRoute(instrument, { serviceOrderCode });
    if (route) { navigate(route); return; }
    // Familia desconocida (falta type/unit): abrir edición para completarlos y
    // resolver la ruta al guardar.
    setStartCalibrationFlow(true);
    setEditModalOpen(true);
  };

  const handleCancelInstrumentModal = () => {
    setStartCalibrationFlow(false);
    setEditModalOpen(false);
  };

  // ✅ NUEVO: Handler para recargar datos cuando el modal de edición se guarda
  const handleInstrumentUpdated = async () => {
    setEditModalOpen(false);
    try {
      // Solo necesitamos recargar el objeto 'instrument'
      const inst = await getInstrument(instrumentId);
      setInstrument(inst);

      if (startCalibrationFlow) {
        const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
        const q = serviceOrderCode ? `?serviceOrderCode=${encodeURIComponent(serviceOrderCode)}` : '';
        setStartCalibrationFlow(false);
        // Ramificar por magnitud: eléctrico → pantalla eléctrica, presión →
        // selector de presión. Si no se resuelve la familia, cae al de presión
        // (que incluye el selector con todas las familias).
        const route = resolveCalibrationRoute(inst, { serviceOrderCode });
        navigate(route || `/ops/calibrations/pressure/manometer/${instrumentId}/template${q}`);
      }
    } catch (e) {
      console.error('[InstrumentDetails] Error recargando instrumento:', e);
    }
  };
  
  // ① handler para refrescar ficha tras cerrar
  const handleCalibrationClosed = async () => {
    try {
      // Recargar instrumento (por si cambió last/next calibration)
      const inst = await getInstrument(instrumentId);
      setInstrument(inst);

      // Recargar calibraciones y resultados
      const all = await listCalibrations();
      const mine = (all || [])
        .filter(r => r.instrument?.id === instrumentId)
        .sort((a, b) => new Date(b.date || 0) - new Date(a, b));
      setCals(mine);

      const rows = await findLatestMeasurementsFor(mine);
      setLastCalMeasurements(rows.measurements || []);
      setLastCalUsed(rows.cal || null);

      if (rows.cal) {
        const blocks = await listBlocks(rows.cal.id);
        setLastCalBlocks(blocks || []);
      } else {
        setLastCalBlocks([]);
      }

      // Volver a la ficha
      openDetails();
    } catch (e) {
      console.error('[handleCalibrationClosed] refresh error', e);
    }
  };
  // ────────────────────────────────────────────────────────────────────────────
  
  // ────────────────────────────────────────────────────────────────────────────
  // Agrupar mediciones por bloque
  // ────────────────────────────────────────────────────────────────────────────
  const measurementsByBlock = useMemo(() => {
    if (!lastCalMeasurements?.length || !lastCalBlocks?.length) return [];

    // Agrupar mediciones por block_id
    const grouped = new Map();
    lastCalMeasurements.forEach(m => {
      const blockId = m.block_id;
      if (!blockId) return; // Ignorar mediciones sin bloque
      if (!grouped.has(blockId)) grouped.set(blockId, []);
      grouped.get(blockId).push(m);
    });

    // Calcular KPIs por cada bloque
    return Array.from(grouped.entries()).map(([blockId, measurements]) => {
      const block = lastCalBlocks.find(b => b.id === blockId);
      const n = measurements.length;
      const mean = (arr) => arr.reduce((s, x) => s + Number(x || 0), 0) / arr.length;

      const absAvg = mean(measurements.map(m => Math.abs(Number(m.abs_error ?? 0))));
      const relAvg = mean(measurements.map(m => Math.abs(Number(m.rel_error ?? 0)))) * 100;
      const uAvg = mean(measurements.map(m => Number(m.u ?? 0)));

      const unit = measurements[0]?.unit || block?.unit || '';

      return {
        blockId,
        blockName: block?.display_name || block?.mode || 'Sin nombre',
        blockType: block?.block_type || 'standard_comparison',
        unit,
        n,
        absAvg,
        relAvg,
        uAvg,
        measurements
      };
    });
  }, [lastCalMeasurements, lastCalBlocks]);

  // ────────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando instrumento…</p>
          </div>
        </div>
      </main>
    );
  }

  if (err || !instrument) {
    return (
      <main className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
          <h2 className="text-xl font-semibold mb-2">Instrumento no encontrado</h2>
          <p className="text-muted-foreground mb-6">
            {err || `No se pudo encontrar información para el instrumento con ID: ${instrumentId}`}
          </p>
          <Button onClick={handleBackToOrigin}>
            Volver al resumen de operaciones
          </Button>
        </div>
      </main>
    );
  }

  const derivedStatus =
    isExpired(nextDue) ? 'expired' : (instrument?.status || 'active');

  const title =
    [instrument?.brand, instrument?.model].filter(Boolean).join(' ') ||
    instrument?.tag ||
    'Instrumento';

  if (view === 'editor' && calibrationId) {
    return (
      <main className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={openDetails}
              className="flex items-center space-x-2"
            >
              <Icon name="ArrowLeft" size={16} />
              <span>Volver a Ficha</span>
            </Button>
            <h1 className="text-xl font-semibold">Mediciones — {title}</h1>
          </div>
          <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(derivedStatus)}`}>
            <Icon name={getStatusIcon(derivedStatus)} size={16} />
            <span className="capitalize">
              {getStatusLabel(derivedStatus)}
            </span>
          </div>
        </div>

        <MeasurementEditor 
          instrument={instrument}
          instrumentId={instrumentId} 
          calibrationId={calibrationId} 
          preferredBlockType={searchParams.get('blockType') || null}
          onCalibrationClosed={handleCalibrationClosed}
        />
      </main>
    );
  }

  return (
    <main className="p-6 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToOrigin}
            className="flex items-center space-x-2"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Volver</span>
          </Button>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {instrument?.lab_id != null && (
                <span className="text-base font-bold px-2.5 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30">
                  ID #{instrument.lab_id}
                </span>
              )}
              <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            </div>
            <p className="text-muted-foreground">
              {(instrument?.type || '—')}{instrument?.serial ? ` • ${instrument.serial}` : ''}
            </p>
          </div>
        </div>

        <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(derivedStatus)}`}>
          <Icon name={getStatusIcon(derivedStatus)} size={16} />
          <span className="capitalize">
            {getStatusLabel(derivedStatus)}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4">
        <div className="inline-flex rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            onClick={openDetails}
            className={`px-4 py-2 text-sm ${view === 'details' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'} border-r border-border`}
          >
            Ficha
          </button>
          <button
            type="button"
            onClick={() => openEditor(cals?.[0]?.id || '')}
            className={`px-4 py-2 text-sm ${view === 'editor' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'}`}
            title={cals?.[0]?.id ? 'Abrir última calibración' : 'Crea una calibración para iniciar'}
            disabled={!cals?.[0]?.id}
          >
            Mediciones
          </button>
        </div>
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Columna izquierda */}
        <div className="xl:col-span-2 space-y-6">
          {/* Información Básica */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Info" size={20} className="mr-2" />
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">ID (nuestro sistema)</label>
                <p className="text-foreground font-semibold">{instrument?.lab_id != null ? `#${instrument.lab_id}` : '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Cliente</label>
                <p className="text-foreground font-medium">{instrument?.client?.name || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tag</label>
                <p className="text-foreground font-medium">{instrument?.tag || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Marca</label>
                <p className="text-foreground font-medium">{instrument?.brand || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Modelo</label>
                <p className="text-foreground font-medium">{instrument?.model || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Número de Serie</label>
                <p className="text-foreground font-medium">{instrument?.serial || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tipo</label>
                <p className="text-foreground font-medium">{instrument?.type || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de ingreso</label>
                <p className="text-foreground font-medium">{getDateLabel(instrument?.reception_date)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de salida</label>
                <p className="text-foreground font-medium">{getDateLabel(instrument?.departure_date)}</p>
              </div>
            </div>
          </div>

          {/* Cliente & Contacto */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2" />
              Cliente & Contacto
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Compañía</label>
                <p className="text-foreground font-medium">{instrument?.client?.name || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Contacto</label>
                <p className="text-foreground font-medium">{instrument?.client?.contact || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <p className="text-foreground font-medium">{instrument?.client?.email || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Teléfono</label>
                <p className="text-foreground font-medium">{instrument?.client?.phone || '—'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-muted-foreground">Dirección</label>
                <p className="text-foreground font-medium">{instrument?.client?.address || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tax ID / CUIT</label>
                <p className="text-foreground font-medium">{instrument?.client?.tax_id || '—'}</p>
              </div>
            </div>
          </div>

          {/* Especificaciones Técnicas */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Settings" size={20} className="mr-2" />
              Especificaciones Técnicas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Rango</label>
                <p className="text-foreground font-medium">
                  {(instrument?.range_min ?? '—')} – {(instrument?.range_max ?? '—')} {instrument?.unit || ''}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Resolución</label>
                <p className="text-foreground font-medium">{instrument?.resolution ?? '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tolerancia</label>
                <p className="text-foreground font-medium">
                  {instrument?.tolerance != null ? `±${instrument.tolerance} ${instrument?.unit || ''}` : '—'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Estado Físico</label>
                <p className="text-foreground font-medium">{instrument?.status || '—'}</p>
              </div>
            </div>
          </div>

          {/* Información Adicional */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="FileText" size={20} className="mr-2" />
              Información Adicional
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Última Calibración</label>
                <p className="text-foreground font-medium">{formatDate(lastCalDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">N° Certificado</label>
                <p className="text-foreground font-medium">{instrument?.certificatenumber ?? '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Ubicación</label>
                <p className="text-foreground font-medium">{instrument?.location || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de ingreso</label>
                <p className="text-foreground font-medium">{getDateLabel(instrument?.reception_date)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de salida</label>
                <p className="text-foreground font-medium">{getDateLabel(instrument?.departure_date)}</p>
              </div>
            </div>
          </div>

          {/* Movimiento de laboratorio */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Truck" size={20} className="mr-2" />
              Movimiento de laboratorio
            </h3>
            {loadingRemitoInfo ? (
              <p className="text-sm text-muted-foreground">Cargando remito asociado…</p>
            ) : lastRemitoInfo ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Último remito</label>
                  <p className="text-foreground font-medium">{lastRemitoInfo.code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fecha de salida</label>
                  <p className="text-foreground font-medium">{getDateLabel(lastRemitoInfo.date_out)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Estado del remito</label>
                  <p className="text-foreground font-medium">{getMovementStatusLabel(lastRemitoInfo.status)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Cantidad de remitos</label>
                  <p className="text-foreground font-medium">{lastRemitoInfo.count || 1}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Este instrumento todavía no tiene remito asociado.
              </p>
            )}
          </div>

          {/* Resultados de la última calibración con mediciones */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-1 flex items-center">
              <Icon name="Activity" size={20} className="mr-2" />
              Resultados de la última calibración
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {lastCalUsed
                ? <>Mostrando resultados de la calibración del <strong>{formatDate(lastCalUsed.date)}</strong>.</>
                : 'No se encontraron calibraciones con mediciones.'}
            </p>

            {lastCalMeasurements?.length ? (
              <>
                {measurementsByBlock.length > 0 ? (
                  <div className="space-y-6">
                    {measurementsByBlock.map(blockData => (
                      <div key={blockData.blockId} className="border border-border rounded-lg p-4 bg-muted/20">
                        {/* Header del bloque */}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-base font-semibold">{blockData.blockName}</h4>
                            <p className="text-xs text-muted-foreground">
                              {blockData.n} punto{blockData.n !== 1 ? 's' : ''} • {blockData.unit}
                            </p>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                            {blockData.blockType === 'pressure_hysteresis' ? 'Histéresis' : 
                             blockData.blockType === 'chamber_mapping' ? 'Mapeo' : 
                             'Comparación'}
                          </span>
                        </div>

                        {/* KPIs del bloque */}
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
                          <div className="p-3 rounded-lg border border-border bg-card">
                            <p className="text-xs text-muted-foreground">Puntos</p>
                            <p className="text-lg font-semibold">{blockData.n}</p>
                          </div>
                          <div className="p-3 rounded-lg border border-border bg-card">
                            <p className="text-xs text-muted-foreground">Error abs. promedio</p>
                            <p className="text-lg font-semibold">
                              {blockData.absAvg?.toFixed(4)} {blockData.unit}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border border-border bg-card">
                            <p className="text-xs text-muted-foreground">Error rel. promedio</p>
                            <p className="text-lg font-semibold">
                              {blockData.relAvg?.toFixed(3)} %
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border border-border bg-card">
                            <p className="text-xs text-muted-foreground">U (k≈2) promedio</p>
                            <p className="text-lg font-semibold">
                              {blockData.uAvg?.toFixed(4)} {blockData.unit}
                            </p>
                          </div>
                        </div>

                        {/* Mini tabla del bloque */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                              <tr>
                                <th className="text-left px-3 py-2">Setpoint</th>
                                <th className="text-left px-3 py-2">Lectura</th>
                                <th className="text-left px-3 py-2">Error abs.</th>
                                <th className="text-left px-3 py-2">Error rel.</th>
                                <th className="text-left px-3 py-2">U (k)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {blockData.measurements.slice(0, 5).map((m) => (
                                <tr key={m.id}>
                                  <td className="px-3 py-2">{m.setpoint} {blockData.unit}</td>
                                  <td className="px-3 py-2">{m.reading} {blockData.unit}</td>
                                  <td className="px-3 py-2">
                                    {Number(m.abs_error).toFixed(5)} {blockData.unit}
                                  </td>
                                  <td className="px-3 py-2">
                                    {(Math.abs(Number(m.rel_error)) * 100).toFixed(3)} %
                                  </td>
                                  <td className="px-3 py-2">
                                    {Number(m.u).toFixed(5)} (k={m.coverage_k ?? 2})
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {blockData.measurements.length > 5 && (
                            <p className="text-xs text-muted-foreground mt-2 text-center">
                              Mostrando 5 de {blockData.measurements.length} mediciones
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Botón para ver/editar mediciones */}
                    <div className="mt-4">
                      <Button
                        variant="secondary"
                        onClick={() => openEditor(lastCalUsed?.id || cals?.[0]?.id)}
                        className="inline-flex items-center gap-2"
                      >
                        <Icon name="Edit" size={16} />
                        Ver / editar todas las mediciones
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Las mediciones no están organizadas en bloques. 
                    <button 
                      onClick={() => openEditor(lastCalUsed?.id || cals?.[0]?.id)}
                      className="ml-1 text-primary underline"
                    >
                      Ir al editor para reorganizarlas
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                No hay mediciones registradas en las calibraciones de este instrumento. 
                <button 
                  onClick={handleNuevaCalibracion}
                  className="ml-1 text-primary underline"
                >
                  Crea una nueva calibración
                </button> o abre el editor.
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha */}
        <div className="xl:col-span-1 space-y-6">
          {/* Estado de Calibración */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Calendar" size={20} className="mr-2" />
              Estado de Calibración
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Próxima Calibración</label>
                <div className="flex items-center gap-2">
                  <p className={`text-foreground font-medium ${isExpired(nextDue) ? 'text-error' : ''}`}>
                    {formatDate(nextDue)}
                  </p>
                  {isExpired(nextDue) && <Icon name="AlertTriangle" size={16} className="text-error" />}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Intervalo (meses)</label>
                <p className="text-foreground font-medium">{instrument?.calibrationintervalmonths ?? '—'}</p>
              </div>
            </div>
          </div>

          {/* Orden de Trabajo (simple) */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="ClipboardList" size={20} className="mr-2" />
              Orden de Trabajo
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Técnico</label>
                <p className="text-foreground font-medium">
                  {lastCalUsed?.technician_name || '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Archivos del instrumento */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Paperclip" size={20} className="mr-2" />
              Archivos del instrumento
            </h3>

            {loadingFiles ? (
              <p className="text-sm text-muted-foreground">Cargando archivos…</p>
            ) : files?.length ? (
              <ul className="divide-y divide-border">
                {files.map(f => (
                  <li key={f.id} className="py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{f.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(f.created_at)} • {formatSize(f.size)} • {f.mime || '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <a
                        href={f.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2 py-1 text-sm rounded border border-border hover:bg-muted/50"
                        title="Ver / descargar"
                      >
                        <Icon name="Download" size={14} className="mr-1" /> Abrir
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No hay archivos adjuntos.</p>
            )}
          </div>

          {/* Certificados emitidos (aprobados por el jefe) */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Award" size={20} className="mr-2" />
              Certificados emitidos
            </h3>
            {certs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Todavía no hay certificados aprobados para este instrumento.
              </p>
            ) : (
              <ul className="space-y-3">
                {certs.map((c) => (
                  <li key={c.id} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-semibold">N° {c.certificate_number}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.calibration_date ? getDateLabel(c.calibration_date) : '—'}
                        {c.valid_until ? ` · vence ${getDateLabel(c.valid_until)}` : ''}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {c.pdf_url && (
                        <a
                          href={c.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-border text-xs hover:bg-muted/50 transition"
                        >
                          <Icon name="Download" size={14} className="mr-1.5" />
                          Descargar PDF
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setLabelFor(c)}
                        className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-border text-xs hover:bg-muted/50 transition"
                      >
                        🏷 Imprimir etiqueta
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Acciones Rápidas */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="Zap" size={20} className="mr-2" />
              Acciones Rápidas
            </h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleDescargarCertificado}
                disabled={downloading}
                className="w-full inline-flex items-center justify-start px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition disabled:opacity-60"
              >
                <Icon name="Download" size={16} className="mr-2" />
                {downloading ? 'Buscando certificado…' : 'Descargar Certificado'}
              </button>

              <button
                type="button"
                onClick={handleStartCalibration}
                className="w-full inline-flex items-center justify-start px-3 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition disabled:opacity-60"
              >
                <Icon name="PlusCircle" size={16} className="mr-2" />
                Iniciar calibración
              </button>

              <button
                type="button"
                onClick={handleEditarInformacion}
                className="w-full inline-flex items-center justify-start px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition"
              >
                <Icon name="Edit" size={16} className="mr-2" />
                Editar Información
              </button>

              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                title="Para instrumentos especiales: subir un certificado hecho por fuera de la app"
                className="w-full inline-flex items-center justify-start px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Subir certificado (especial)
              </button>

              {uploadedNumber && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
                  Certificado <b>{uploadedNumber}</b> subido. Queda <b>pendiente de aprobación</b> del jefe de laboratorio.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Etiqueta QR del certificado (reimprimible) */}
      <CertLabelModal
        open={!!labelFor}
        certificate={labelFor}
        instrument={instrument}
        onClose={() => setLabelFor(null)}
      />

      {/* Subir certificado externo (instrumentos especiales) */}
      <ExternalCertUploadModal
        open={uploadOpen}
        instrument={instrument}
        onClose={() => setUploadOpen(false)}
        onUploaded={(cert) => setUploadedNumber(cert?.certificate_number || '')}
      />

      {/* --- ✅ MODAL DE EDICIÓN --- */}
      <IntakeInstrumentModal
        open={editModalOpen}
        onClose={handleCancelInstrumentModal}
        mode="edit"
        instrumentId={instrumentId}
        onSaved={handleInstrumentUpdated}
      />
    </main>
  );
};

export default InstrumentDetails;





